import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Plus, MagnifyingGlass, X, Package, Tray, FirstAidKit, PencilSimple, PaperPlaneTilt, Eye, ListChecks } from "@phosphor-icons/react";
import ProductForm from "@/components/ProductForm";
import ItemDetailDialog from "@/components/preinventory/ItemDetailDialog";
import SendToInventoryDialog from "@/components/preinventory/SendToInventoryDialog";
import { StatusBadge, fmtMoney } from "@/components/preinventory/shared";

const FILTERS = [
  { key: "pendientes", label: "Pendientes" },
  { key: "en_preparacion", label: "En preparación" },
  { key: "listo", label: "Listas" },
  { key: "enviado", label: "Enviadas" },
  { key: "all", label: "Todas" },
];

export default function Preinventario() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [checklist, setChecklist] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("pendientes");
  const [detailFor, setDetailFor] = useState(null);
  const [editFor, setEditFor] = useState(null);      // item → ProductForm draft mode
  const [newOpen, setNewOpen] = useState(false);
  const [sendFor, setSendFor] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const reload = async () => {
    const params = {};
    if (q.trim()) params.q = q.trim();
    if (filter !== "all") params.status = filter;
    const [l, s] = await Promise.all([api.get("/preinventory", { params }), api.get("/preinventory/stats")]);
    setItems(l.data || []);
    setStats(s.data);
  };

  useEffect(() => {
    api.get("/preinventory/checklist").then((r) => setChecklist(r.data || [])).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => { reload().catch(() => toast.error("No se pudo cargar Preinventario")); }, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q, filter]);

  // Deep-link desde Hospital Interno: ?item=<id>
  useEffect(() => {
    const iid = searchParams.get("item");
    if (!iid) return;
    api.get(`/preinventory/${iid}`).then((r) => setDetailFor(r.data)).catch(() => {});
    const next = new URLSearchParams(searchParams);
    next.delete("item");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line
  }, []);

  const patchItem = (updated) => {
    setItems((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
    setDetailFor((cur) => (cur && cur.id === updated.id ? updated : cur));
    api.get("/preinventory/stats").then((r) => setStats(r.data)).catch(() => {});
  };

  const toggleCheck = async (item, key, val) => {
    try {
      const { data } = await api.patch(`/preinventory/${item.id}`, { checklist: { [key]: val } });
      patchItem(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo actualizar");
    }
  };

  const saveDraft = (item) => async (payload) => {
    const { data } = await api.patch(`/preinventory/${item.id}`, { draft: payload });
    patchItem(data);
  };

  const createManual = async (payload) => {
    const { data } = await api.post("/preinventory", { draft: payload });
    toast.success(`Ficha ${data.code} creada`);
    setNewOpen(false);
    await reload();
    setDetailFor(data);
  };

  const draftAsProduct = (item) => {
    const d = { ...(item.draft || {}) };
    delete d.id;
    return d;
  };

  const counts = useMemo(() => stats || { total: 0, en_preparacion: 0, listo: 0, enviado: 0 }, [stats]);

  return (
    <div className="p-6 sm:p-8 space-y-6" data-testid="preinventory-page">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="font-mono-data text-xs tracking-widest text-zinc-500 mb-1">CAPA PREVIA AL INVENTARIO</div>
          <h1 className="font-heading text-4xl font-extrabold tracking-tight flex items-center gap-3">
            <Tray size={32} weight="duotone" className="text-[#002FA7]" /> Preinventario
          </h1>
          <p className="text-zinc-500 mt-1 text-sm max-w-2xl">
            Equipos dados de alta en Hospital Interno o compras directas. Aquí se limpian, se fotografían,
            se capturan costo y tiers de precio y se completan los datos de tienda antes de entrar a Inventario.
          </p>
        </div>
        <Button onClick={() => setNewOpen(true)} className="bg-[#002FA7] hover:bg-[#0043c2] gap-2" data-testid="new-preinventory-btn">
          <Plus size={16} weight="bold" /> Nueva ficha
        </Button>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="preinventory-stats">
        <Stat label="Total fichas" value={counts.total} highlight />
        <Stat label="En preparación" value={counts.en_preparacion} cls="border-amber-200 bg-amber-50 text-amber-800" />
        <Stat label="Listas para inventario" value={counts.listo} cls="border-emerald-200 bg-emerald-50 text-emerald-800" />
        <Stat label="Enviadas" value={counts.enviado} cls="border-zinc-200 bg-zinc-50 text-zinc-700" />
      </div>

      <div className="flex gap-3 flex-wrap items-center">
        <div className="relative flex-1 min-w-[260px] max-w-md">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por PRE-, PAT-, nombre, marca, serie…" className="pl-8 pr-8" data-testid="preinventory-search" />
          {q && <button onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700"><X size={14} /></button>}
        </div>
        <div className="flex gap-1 flex-wrap" data-testid="preinventory-filters">
          {FILTERS.map((f) => (
            <button key={f.key} type="button" onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${filter === f.key ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white text-zinc-700 border-zinc-200 hover:border-[#002FA7]"}`}
              data-testid={`filter-${f.key}`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="border border-zinc-200 rounded-md overflow-hidden bg-white">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-[10px] uppercase tracking-widest text-zinc-500">
            <tr>
              <th className="text-left px-4 py-2.5 font-semibold">Ficha</th>
              <th className="text-left px-4 py-2.5 font-semibold">Equipo</th>
              <th className="text-left px-4 py-2.5 font-semibold">Checklist</th>
              <th className="text-left px-4 py-2.5 font-semibold">Preparación</th>
              <th className="text-right px-4 py-2.5 font-semibold">Costo / Tienda</th>
              <th className="text-left px-4 py-2.5 font-semibold">Estado</th>
              <th className="px-4 py-2.5" />
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-zinc-400 text-sm" data-testid="preinventory-empty">
                No hay fichas en este filtro. Da de alta un equipo desde Hospital Interno o crea una ficha nueva.
              </td></tr>
            )}
            {items.map((it) => {
              const d = it.draft || {};
              const r = it.readiness || { filled: 0, total: 1, missing: [] };
              const photo = d.photos_url || (d.photos && d.photos[0]);
              const sent = !!it.sent;
              return (
                <tr key={it.id} className="border-t border-zinc-100 hover:bg-zinc-50/60 align-top" data-testid={`preinventory-row-${it.id}`}>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <button type="button" onClick={() => setDetailFor(it)} className="font-mono text-[#002FA7] font-semibold hover:underline" data-testid={`open-item-${it.id}`}>{it.code}</button>
                    {it.patient_code && (
                      <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded bg-cyan-50 text-cyan-800 border border-cyan-200">
                        <FirstAidKit size={10} weight="bold" /> {it.patient_code}
                      </div>
                    )}
                    <div className="text-[10px] text-zinc-400 mt-1">{new Date(it.created_at).toLocaleDateString("es-MX", { day: "2-digit", month: "short" })} · {it.created_by_name}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <span className="w-11 h-11 rounded bg-zinc-100 border border-zinc-200 flex items-center justify-center overflow-hidden shrink-0">
                        {photo ? <img src={photo} alt="" className="w-full h-full object-cover" /> : <Package size={16} className="text-zinc-300" />}
                      </span>
                      <div className="min-w-0">
                        <div className="font-semibold text-zinc-900 truncate max-w-[280px]" title={d.name}>{d.name || <span className="italic text-zinc-400">Sin nombre</span>}</div>
                        <div className="text-[11px] text-zinc-500 truncate max-w-[280px]">{[d.brand, d.category, d.stock ? `${d.stock} pz` : null].filter(Boolean).join(" · ")}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1">
                      {checklist.map((c) => (
                        <label key={c.key} className="inline-flex items-center gap-1.5 text-[11px] text-zinc-700 cursor-pointer">
                          <Checkbox checked={!!it.checklist?.[c.key]} disabled={sent} onCheckedChange={(v) => toggleCheck(it, c.key, !!v)} className="h-3.5 w-3.5" data-testid={`check-${c.key}-${it.id}`} />
                          {c.label}
                        </label>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-1.5 bg-zinc-200 rounded-full overflow-hidden">
                        <div className={`h-full ${r.ready ? "bg-emerald-500" : "bg-amber-500"}`} style={{ width: `${Math.round((r.filled / r.total) * 100)}%` }} />
                      </div>
                      <span className="text-[11px] font-mono-data text-zinc-600">{r.filled}/{r.total}</span>
                    </div>
                    {r.spec_required_total > 0 && (
                      <div className={`text-[10px] font-mono-data font-semibold mt-1 inline-flex items-center gap-1 ${r.spec_required_filled === r.spec_required_total ? "text-emerald-700" : "text-[#E0000F]"}`} data-testid={`spec-progress-${it.id}`}>
                        <ListChecks size={11} weight="bold" /> Ficha técnica {r.spec_required_filled}/{r.spec_required_total}
                      </div>
                    )}
                    {!sent && r.missing.length > 0 && (
                      <div className="text-[10px] text-amber-700 mt-1 max-w-[220px] truncate" title={r.missing.map((m) => m.label).join(", ")}>
                        Falta: {r.missing.slice(0, 3).map((m) => m.label).join(", ")}{r.missing.length > 3 ? ` +${r.missing.length - 3}` : ""}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right numeric whitespace-nowrap">
                    <div className="text-zinc-600 text-xs">{fmtMoney(d.cost, d.cost_currency)}</div>
                    <div className="font-semibold">{fmtMoney(d.store_price, d.sale_currency)}</div>
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={it.status} /></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setDetailFor(it)} title="Ver ficha" className="h-8 w-8 p-0" data-testid={`detail-${it.id}`}><Eye size={15} /></Button>
                      {!sent && <Button size="sm" variant="ghost" onClick={() => setEditFor(it)} title="Editar ficha completa" className="h-8 w-8 p-0" data-testid={`edit-${it.id}`}><PencilSimple size={15} /></Button>}
                      {!sent && (
                        <Button size="sm" onClick={() => setSendFor(it)} className={`h-8 gap-1 ${r.ready ? "bg-[#002FA7] hover:bg-[#002277] text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`} data-testid={`send-${it.id}`}>
                          <PaperPlaneTilt size={13} weight="bold" /> Enviar
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {detailFor && (
        <ItemDetailDialog
          item={detailFor}
          checklist={checklist}
          onClose={() => setDetailFor(null)}
          onChanged={patchItem}
          onEdit={(it) => setEditFor(it)}
          onSend={(it) => setSendFor(it)}
          onDeleted={() => { setDetailFor(null); reload(); }}
        />
      )}

      {editFor && (
        <ProductForm
          draftMode
          draftTitle={`Ficha ${editFor.code} — datos para inventario`}
          product={draftAsProduct(editFor)}
          categories={categories}
          onClose={() => setEditFor(null)}
          onSaved={() => setEditFor(null)}
          onSubmitDraft={saveDraft(editFor)}
          onCategoriesChanged={() => api.get("/categories").then((r) => setCategories(r.data || []))}
        />
      )}

      {newOpen && (
        <ProductForm
          draftMode
          draftTitle="Nueva ficha de preinventario"
          draftSubmitLabel="Crear ficha"
          product={{ stock: 1, condition: "reacondicionado", low_stock_threshold: 1, category: categories[0] || "" }}
          categories={categories}
          onClose={() => setNewOpen(false)}
          onSaved={() => {}}
          onSubmitDraft={createManual}
          onCategoriesChanged={() => api.get("/categories").then((r) => setCategories(r.data || []))}
        />
      )}

      {sendFor && (
        <SendToInventoryDialog
          item={sendFor}
          onClose={() => setSendFor(null)}
          onSent={(res) => { patchItem(res.item); setDetailFor(null); reload(); }}
        />
      )}
    </div>
  );
}

function Stat({ label, value, highlight, cls }) {
  return (
    <div className={`p-3 rounded-md border ${highlight ? "border-[#002FA7]/30 bg-[#002FA7]/5" : cls || "border-zinc-200 bg-white"}`}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">{label}</div>
      <div className={`mt-1 font-heading text-2xl font-extrabold numeric ${highlight ? "text-[#002FA7]" : ""}`}>{value || 0}</div>
    </div>
  );
}
